
import React, { memo } from "react";
import { TabsContent } from "@/components/ui/tabs";
import { MovieListContainer } from "@/components/movie/list";
import { MoviesByDirector } from "@/types/movie";
import { type MovieListName } from "@/hooks/search/types/listConstants";

interface MovieTabContentProps {
  value: MovieListName;
  moviesByDirector: MoviesByDirector;
  emptyMessage: string | React.ReactNode;
  currentTab: MovieListName;
  direction: "left" | "right" | null;
  viewMode: "grid" | "list";
  isAuthenticated: boolean;
  onOpenMovieDetails: (tmdbId: number) => void;
}

// Using memo to prevent unnecessary re-renders when props don't change
const MovieTabContent = memo<MovieTabContentProps>(({ 
  value, 
  moviesByDirector, 
  emptyMessage, 
  currentTab,
  direction,
  viewMode,
  isAuthenticated,
  onOpenMovieDetails
}) => {
  // Enhanced animation classes with combination of fade and scale for smoother transitions
  const getAnimationClasses = () => {
    // Only apply animation when this tab is active
    if (value !== currentTab) return "";
    
    // Apply different animations based on direction
    if (direction === "left") {
      return "animate-slide-in-left";
    } else if (direction === "right") {
      return "animate-slide-in-right";
    } else {
      // Default to a gentle fade-in with slight scale effect when no direction is specified
      return "animate-fade-in";
    }
  };

  return (
    <TabsContent 
      value={value} 
      className="mt-1 md:mt-2 bg-transparent"
    >
      <div className={`transition-all duration-300 ease-in-out min-h-[200px] ${getAnimationClasses()}`}>
        <MovieListContainer
          moviesByDirector={moviesByDirector}
          emptyMessage={emptyMessage}
          currentTab={currentTab}
          viewMode={viewMode}
          isAuthenticated={isAuthenticated}
          onOpenMovieDetails={onOpenMovieDetails}
        />
      </div>
    </TabsContent>
  );
});

MovieTabContent.displayName = "MovieTabContent";

export default MovieTabContent;
